import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Switch } from 'app/components/ui/switch';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'app/components/ui/select';
import { Separator } from 'app/components/ui/separator';
import { Textarea } from 'app/components/ui/textarea';
import { Button } from 'app/components/ui/button';
import { Badge } from 'app/components/ui/badge';
import {
  Bell,
  Link,
  Palette,
  Eye,
  EyeOff,
  X,
  Calendar,
  Smartphone,
  Monitor,
  Zap,
  Clock,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Copy,
  Save,
} from 'lucide-react';
import { useSettingsSlice } from '../slice';

interface NotificationHeaderSettingsProps {
  form: UseFormReturn<any>;
}

// Helper function to create payload with is_public for all keys
const createSettingsPayload = (formData: any) => {
  const payload: Record<string, { option_value: any; is_public: number }> = {};

  // Add is_public: 1 for all notification header settings
  const settingsKeys = [
    'notification_header_enabled',
    'notification_header_message',
    'notification_header_link_text',
    'notification_header_link_url',
    'notification_header_icon',
    'notification_header_background_color',
    'notification_header_text_color',
    'notification_header_link_color',
    'notification_header_animation',
    'notification_header_dismissible',
    'notification_header_show_on_mobile',
    'notification_header_show_countdown',
    'notification_header_start_date',
    'notification_header_end_date',
  ];

  settingsKeys.forEach(key => {
    if (formData[key] !== undefined) {
      let value = formData[key];

      // Convert string "1" and "0" to actual numbers 1 and 0
      if (value === '1') value = 1;
      if (value === '0') value = 0;

      payload[key] = {
        option_value: value,
        is_public: 1,
      };
    }
  });

  return payload;
};

export default function NotificationHeaderSettings({
  form,
}: NotificationHeaderSettingsProps) {
  const [showPreview, setShowPreview] = useState(false);
  const [previewDismissed, setPreviewDismissed] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');

  const { useSaveSiteSettingsMutation } = useSettingsSlice();
  const [saveSiteSettings, { isLoading: isUpdating }] =
    useSaveSiteSettingsMutation();

  const handleCopyLink = () => {
    const linkUrl = form.watch('notification_header_link_url');
    if (linkUrl) {
      navigator.clipboard.writeText(linkUrl);
      // You could add a toast notification here
    }
  };

  // Function to handle settings update with is_public for all keys
  const handleUpdateSettings = async () => {
    try {
      setUpdateStatus('idle');
      const formData = form.getValues();
      const payload = createSettingsPayload(formData);

      console.log('Settings payload with is_public:', payload);

      // Use the Redux mutation to save settings
      const result = await saveSiteSettings(payload).unwrap();
      console.log('Settings updated successfully:', result);

      setUpdateStatus('success');
      return result;
    } catch (error) {
      console.error('Error updating settings:', error);
      setUpdateStatus('error');
      throw error;
    }
  };

  const renderPreview = () => {
    if (
      !Boolean(form.watch('notification_header_enabled')) ||
      previewDismissed
    ) {
      return null;
    }

    const message =
      form.watch('notification_header_message') || 'Notification message';
    const linkText =
      form.watch('notification_header_link_text') || 'Learn More';
    const linkUrl = form.watch('notification_header_link_url') || '#';
    const bgColor =
      form.watch('notification_header_background_color') || '#f27a24';
    const textColor = form.watch('notification_header_text_color') || '#ffffff';
    const linkColor = form.watch('notification_header_link_color') || '#ffffff';
    const icon = form.watch('notification_header_icon') || '🎉';
    const dismissible = Boolean(form.watch('notification_header_dismissible'));

    return (
      <div
        className="w-full p-3 text-center relative"
        style={{
          backgroundColor: bgColor,
          color: textColor,
        }}
      >
        <div className="flex items-center justify-center gap-2">
          <span className="text-lg">{icon}</span>
          <span className="text-sm font-medium">{message}</span>
          <a
            href={linkUrl}
            className="text-sm font-semibold underline hover:no-underline transition-all"
            style={{ color: linkColor }}
          >
            {linkText}
          </a>
        </div>
        {dismissible && (
          <button
            onClick={() => setPreviewDismissed(true)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-200"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Notification Header Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notification Header Configuration
          </CardTitle>
          <CardDescription>
            Configure a banner notification that appears at the top of your
            website.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="notification_header_enabled">
                Enable Notification Header
              </Label>
              <p className="text-sm text-gray-500">
                Show a notification banner at the top of your website
              </p>
            </div>
            <Switch
              id="notification_header_enabled"
              checked={Boolean(form.watch('notification_header_enabled'))}
              onCheckedChange={value =>
                form.setValue('notification_header_enabled', value ? 1 : 0)
              }
            />
          </div>

          {Boolean(form.watch('notification_header_enabled')) && (
            <>
              <Separator />

              <div className="space-y-2">
                <Label htmlFor="notification_header_message">
                  Notification Message
                </Label>
                <Textarea
                  id="notification_header_message"
                  {...form.register('notification_header_message')}
                  placeholder="Enter your notification message..."
                  rows={2}
                />
                <p className="text-xs text-muted-foreground">
                  The main message to display in the notification banner
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="notification_header_link_text">
                    Link Text
                  </Label>
                  <Input
                    id="notification_header_link_text"
                    {...form.register('notification_header_link_text')}
                    placeholder="Learn More"
                  />
                  <p className="text-xs text-muted-foreground">
                    Text for the call-to-action link
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notification_header_link_url">Link URL</Label>
                  <div className="relative">
                    <Input
                      id="notification_header_link_url"
                      {...form.register('notification_header_link_url')}
                      placeholder="/new-products"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleCopyLink}
                      className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    URL where the link should redirect
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notification_header_icon">Icon (Emoji)</Label>
                <Input
                  id="notification_header_icon"
                  {...form.register('notification_header_icon')}
                  placeholder="🎉"
                  maxLength={10}
                />
                <p className="text-xs text-muted-foreground">
                  Emoji or icon to display with the message
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Visual Settings */}
      {Boolean(form.watch('notification_header_enabled')) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Visual Settings
            </CardTitle>
            <CardDescription>
              Customize the appearance of your notification banner.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="notification_header_background_color">
                  Background Color
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="notification_header_background_color"
                    {...form.register('notification_header_background_color')}
                    placeholder="#f27a24"
                    value={
                      form.watch('notification_header_background_color') || ''
                    }
                    onChange={e => {
                      const value = e.target.value;
                      // Ensure it starts with # and is valid hex
                      if (value && !value.startsWith('#')) {
                        form.setValue(
                          'notification_header_background_color',
                          `#${value}`,
                        );
                      } else {
                        form.setValue(
                          'notification_header_background_color',
                          value,
                        );
                      }
                    }}
                  />
                  <input
                    type="color"
                    className="w-10 h-10 rounded border cursor-pointer"
                    value={
                      form.watch('notification_header_background_color') ||
                      '#f27a24'
                    }
                    onChange={e => {
                      form.setValue(
                        'notification_header_background_color',
                        e.target.value,
                      );
                    }}
                    title="Pick background color"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Hex color code (e.g., #f27a24)
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notification_header_text_color">
                  Text Color
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="notification_header_text_color"
                    {...form.register('notification_header_text_color')}
                    placeholder="#ffffff"
                    value={form.watch('notification_header_text_color') || ''}
                    onChange={e => {
                      const value = e.target.value;
                      // Ensure it starts with # and is valid hex
                      if (value && !value.startsWith('#')) {
                        form.setValue(
                          'notification_header_text_color',
                          `#${value}`,
                        );
                      } else {
                        form.setValue('notification_header_text_color', value);
                      }
                    }}
                  />
                  <input
                    type="color"
                    className="w-10 h-10 rounded border cursor-pointer"
                    value={
                      form.watch('notification_header_text_color') || '#ffffff'
                    }
                    onChange={e => {
                      form.setValue(
                        'notification_header_text_color',
                        e.target.value,
                      );
                    }}
                    title="Pick text color"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Hex color code (e.g., #ffffff)
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notification_header_link_color">
                  Link Color
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="notification_header_link_color"
                    {...form.register('notification_header_link_color')}
                    placeholder="#ffffff"
                    value={form.watch('notification_header_link_color') || ''}
                    onChange={e => {
                      const value = e.target.value;
                      // Ensure it starts with # and is valid hex
                      if (value && !value.startsWith('#')) {
                        form.setValue(
                          'notification_header_link_color',
                          `#${value}`,
                        );
                      } else {
                        form.setValue('notification_header_link_color', value);
                      }
                    }}
                  />
                  <input
                    type="color"
                    className="w-10 h-10 rounded border cursor-pointer"
                    value={
                      form.watch('notification_header_link_color') || '#ffffff'
                    }
                    onChange={e => {
                      form.setValue(
                        'notification_header_link_color',
                        e.target.value,
                      );
                    }}
                    title="Pick link color"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Hex color code (e.g., #ffffff)
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notification_header_animation">Animation</Label>
              <Select
                value={form.watch('notification_header_animation')}
                onValueChange={value =>
                  form.setValue('notification_header_animation', value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select animation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="slide-down">Slide Down</SelectItem>
                  <SelectItem value="fade-in">Fade In</SelectItem>
                  <SelectItem value="bounce">Bounce</SelectItem>
                  <SelectItem value="marquee">Marquee</SelectItem>
                  <SelectItem value="none">No Animation</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                How the notification banner appears
              </p>
            </div>

            {/* Preset Color Combinations */}
            <div className="space-y-3">
              <Label>Preset Color Combinations</Label>
              <p className="text-xs text-muted-foreground">
                Click on any preset to apply professional color combinations
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {/* Professional Blue */}
                <button
                  type="button"
                  onClick={() => {
                    form.setValue(
                      'notification_header_background_color',
                      '#2563eb',
                    );
                    form.setValue('notification_header_text_color', '#ffffff');
                    form.setValue('notification_header_link_color', '#fbbf24');
                  }}
                  className="p-3 rounded-lg border border-gray-200 hover:border-blue-300 transition-all hover:shadow-md"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-700">
                        Professional Blue
                      </span>
                      <div className="flex gap-1">
                        <div className="w-4 h-4 rounded bg-blue-600 border border-gray-300"></div>
                        <div className="w-4 h-4 rounded bg-white border border-gray-300"></div>
                        <div className="w-4 h-4 rounded bg-amber-400 border border-gray-300"></div>
                      </div>
                    </div>
                    <div className="h-8 rounded bg-blue-600 text-white text-xs flex items-center justify-center font-medium">
                      Sample Text
                    </div>
                  </div>
                </button>

                {/* Modern Green */}
                <button
                  type="button"
                  onClick={() => {
                    form.setValue(
                      'notification_header_background_color',
                      '#059669',
                    );
                    form.setValue('notification_header_text_color', '#ffffff');
                    form.setValue('notification_header_link_color', '#fef3c7');
                  }}
                  className="p-3 rounded-lg border border-gray-200 hover:border-green-300 transition-all hover:shadow-md"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-700">
                        Modern Green
                      </span>
                      <div className="flex gap-1">
                        <div className="w-4 h-4 rounded bg-green-600 border border-gray-300"></div>
                        <div className="w-4 h-4 rounded bg-white border border-gray-300"></div>
                        <div className="w-4 h-4 rounded bg-amber-100 border border-gray-300"></div>
                      </div>
                    </div>
                    <div className="h-8 rounded bg-green-600 text-white text-xs flex items-center justify-center font-medium">
                      Sample Text
                    </div>
                  </div>
                </button>

                {/* Elegant Purple */}
                <button
                  type="button"
                  onClick={() => {
                    form.setValue(
                      'notification_header_background_color',
                      '#7c3aed',
                    );
                    form.setValue('notification_header_text_color', '#ffffff');
                    form.setValue('notification_header_link_color', '#fbbf24');
                  }}
                  className="p-3 rounded-lg border border-gray-200 hover:border-purple-300 transition-all hover:shadow-md"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-700">
                        Elegant Purple
                      </span>
                      <div className="flex gap-1">
                        <div className="w-4 h-4 rounded bg-purple-600 border border-gray-300"></div>
                        <div className="w-4 h-4 rounded bg-white border border-gray-300"></div>
                        <div className="w-4 h-4 rounded bg-amber-400 border border-gray-300"></div>
                      </div>
                    </div>
                    <div className="h-8 rounded bg-purple-600 text-white text-xs flex items-center justify-center font-medium">
                      Sample Text
                    </div>
                  </div>
                </button>

                {/* Warm Orange */}
                <button
                  type="button"
                  onClick={() => {
                    form.setValue(
                      'notification_header_background_color',
                      '#ea580c',
                    );
                    form.setValue('notification_header_text_color', '#ffffff');
                    form.setValue('notification_header_link_color', '#fef3c7');
                  }}
                  className="p-3 rounded-lg border border-gray-200 hover:border-orange-300 transition-all hover:shadow-md"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-700">
                        Warm Orange
                      </span>
                      <div className="flex gap-1">
                        <div className="w-4 h-4 rounded bg-orange-600 border border-gray-300"></div>
                        <div className="w-4 h-4 rounded bg-white border border-gray-300"></div>
                        <div className="w-4 h-4 rounded bg-amber-100 border border-gray-300"></div>
                      </div>
                    </div>
                    <div className="h-8 rounded bg-orange-600 text-white text-xs flex items-center justify-center font-medium">
                      Sample Text
                    </div>
                  </div>
                </button>

                {/* Corporate Gray */}
                <button
                  type="button"
                  onClick={() => {
                    form.setValue(
                      'notification_header_background_color',
                      '#374151',
                    );
                    form.setValue('notification_header_text_color', '#ffffff');
                    form.setValue('notification_header_link_color', '#60a5fa');
                  }}
                  className="p-3 rounded-lg border border-gray-200 hover:border-gray-400 transition-all hover:shadow-md"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-700">
                        Corporate Gray
                      </span>
                      <div className="flex gap-1">
                        <div className="w-4 h-4 rounded bg-gray-700 border border-gray-300"></div>
                        <div className="w-4 h-4 rounded bg-white border border-gray-300"></div>
                        <div className="w-4 h-4 rounded bg-blue-400 border border-gray-300"></div>
                      </div>
                    </div>
                    <div className="h-8 rounded bg-gray-700 text-white text-xs flex items-center justify-center font-medium">
                      Sample Text
                    </div>
                  </div>
                </button>

                {/* Premium Gold */}
                <button
                  type="button"
                  onClick={() => {
                    form.setValue(
                      'notification_header_background_color',
                      '#92400e',
                    );
                    form.setValue('notification_header_text_color', '#ffffff');
                    form.setValue('notification_header_link_color', '#fef3c7');
                  }}
                  className="p-3 rounded-lg border border-gray-200 hover:border-amber-300 transition-all hover:shadow-md"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-700">
                        Premium Gold
                      </span>
                      <div className="flex gap-1">
                        <div className="w-4 h-4 rounded bg-amber-800 border border-gray-300"></div>
                        <div className="w-4 h-4 rounded bg-white border border-gray-300"></div>
                        <div className="w-4 h-4 rounded bg-amber-100 border border-gray-300"></div>
                      </div>
                    </div>
                    <div className="h-8 rounded bg-amber-800 text-white text-xs flex items-center justify-center font-medium">
                      Sample Text
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Behavior Settings */}
      {Boolean(form.watch('notification_header_enabled')) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Behavior Settings
            </CardTitle>
            <CardDescription>
              Configure how the notification banner behaves.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="notification_header_dismissible">
                    Dismissible
                  </Label>
                  <p className="text-sm text-gray-500">
                    Allow users to close the notification
                  </p>
                </div>
                <Switch
                  id="notification_header_dismissible"
                  checked={Boolean(
                    form.watch('notification_header_dismissible'),
                  )}
                  onCheckedChange={value =>
                    form.setValue(
                      'notification_header_dismissible',
                      value ? 1 : 0,
                    )
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="notification_header_show_on_mobile">
                    Show on Mobile
                  </Label>
                  <p className="text-sm text-gray-500">
                    Display notification on mobile devices
                  </p>
                </div>
                <Switch
                  id="notification_header_show_on_mobile"
                  checked={Boolean(
                    form.watch('notification_header_show_on_mobile'),
                  )}
                  onCheckedChange={value =>
                    form.setValue(
                      'notification_header_show_on_mobile',
                      value ? 1 : 0,
                    )
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="notification_header_show_countdown">
                    Show Countdown
                  </Label>
                  <p className="text-sm text-gray-500">
                    Display countdown timer if end date is set
                  </p>
                </div>
                <Switch
                  id="notification_header_show_countdown"
                  checked={Boolean(
                    form.watch('notification_header_show_countdown'),
                  )}
                  onCheckedChange={value =>
                    form.setValue(
                      'notification_header_show_countdown',
                      value ? 1 : 0,
                    )
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scheduling */}
      {Boolean(form.watch('notification_header_enabled')) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Scheduling
            </CardTitle>
            <CardDescription>
              Set when the notification should be displayed.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="notification_header_start_date">
                  Start Date
                </Label>
                <Input
                  id="notification_header_start_date"
                  type="datetime-local"
                  {...form.register('notification_header_start_date')}
                />
                <p className="text-xs text-muted-foreground">
                  When to start showing the notification (leave empty for
                  immediate)
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notification_header_end_date">End Date</Label>
                <Input
                  id="notification_header_end_date"
                  type="datetime-local"
                  {...form.register('notification_header_end_date')}
                />
                <p className="text-xs text-muted-foreground">
                  When to stop showing the notification (leave empty for
                  indefinite)
                </p>
              </div>
            </div>

            {(form.watch('notification_header_start_date') ||
              form.watch('notification_header_end_date')) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-blue-800 font-medium">
                      Scheduling Active
                    </p>
                    <p className="text-sm text-blue-700 mt-1">
                      {form.watch('notification_header_start_date') && (
                        <span>
                          Starts:{' '}
                          {new Date(
                            form.watch('notification_header_start_date'),
                          ).toLocaleString()}
                        </span>
                      )}
                      {form.watch('notification_header_start_date') &&
                        form.watch('notification_header_end_date') &&
                        ' | '}
                      {form.watch('notification_header_end_date') && (
                        <span>
                          Ends:{' '}
                          {new Date(
                            form.watch('notification_header_end_date'),
                          ).toLocaleString()}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Preview */}
      {Boolean(form.watch('notification_header_enabled')) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Preview
            </CardTitle>
            <CardDescription>
              See how your notification banner will look.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border rounded-lg overflow-hidden">
              {renderPreview()}
              <div className="p-4 bg-gray-50">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>Website Header</span>
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <Monitor className="w-4 h-4" />
                      Desktop
                    </span>
                    <span className="flex items-center gap-1">
                      <Smartphone className="w-4 h-4" />
                      Mobile
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {previewDismissed && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPreviewDismissed(false)}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Show Preview Again
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Best Practices */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Best Practices
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm text-green-800 font-medium">
                  Notification Header Best Practices
                </p>
                <ul className="text-sm text-green-700 mt-2 space-y-1">
                  <li>• Keep messages short and actionable</li>
                  <li>• Use clear call-to-action text</li>
                  <li>• Choose colors that match your brand</li>
                  <li>
                    • Set appropriate start/end dates for time-sensitive
                    messages
                  </li>
                  <li>• Test on both desktop and mobile devices</li>
                  <li>• Don't overuse - reserve for important announcements</li>
                  <li>• Ensure good contrast for accessibility</li>
                  <li>
                    • Consider user experience - make dismissible for
                    non-critical messages
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Warning */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Important Notes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="text-sm text-yellow-800 font-medium">
                  Usage Guidelines
                </p>
                <ul className="text-sm text-yellow-700 mt-2 space-y-1">
                  <li>
                    • Notification headers should be used sparingly for
                    important announcements
                  </li>
                  <li>• Avoid using for regular marketing messages</li>
                  <li>
                    • Consider the impact on user experience and page layout
                  </li>
                  <li>• Test thoroughly before enabling on production</li>
                  <li>• Monitor user engagement and feedback</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fixed Save Button - Bottom Right */}
      <div className="fixed bottom-14 right-6 z-50">
        <div className="flex flex-col items-end gap-3">
          {/* Status Feedback */}
          {updateStatus === 'success' && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Settings saved successfully!
                </span>
              </div>
            </div>
          )}

          {updateStatus === 'error' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Failed to save settings. Please try again.
                </span>
              </div>
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={handleUpdateSettings}
            className="shadow-lg hover:shadow-xl transition-all duration-200"
            variant="default"
            disabled={isUpdating}
            size="lg"
          >
            {isUpdating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}
